import { useState } from 'react';
import { Building2, FileText, Globe, Info, Mail, Phone, Plus, Receipt, X } from 'lucide-react';
import { Card, CardContent, EmptyState, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@mustauto/ui/web';
import { useAuth } from '@/hooks/useAuth';
import { useSuppliers } from '@/hooks/useSuppliers';
import type { Supplier } from '@/hooks/useSuppliers';
import { usePurchaseOrders } from '@/hooks/usePurchaseOrders';
import { useInventoryInsights } from '@/hooks/useInventoryInsights';
import { formatReais } from '@/lib/utils';
import { POStatusBadge } from './StatusBadge';
import { maskCnpj, maskPhone, isValidCnpj as isValidCnpjStrict } from '@mustauto/utils';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { usePayables, PAYABLE_STATUS_LABELS, type PayableStatus } from '@/hooks/usePayables';
import { useCan } from '@/hooks/useCan';

const PAYABLE_BADGE_VARIANT: Record<PayableStatus, 'success' | 'destructive' | 'warning' | 'secondary'> = {
  paid: 'success',
  overdue: 'destructive',
  partial: 'warning',
  open: 'secondary',
};

const PARTNER_BADGE_CLASSES =
  'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';

// --- Masks ---
// `maskCnpj` and `maskPhone` come from @mustauto/utils (shared with backend,
// mobile, web-partner). The validations below are deliberately lenient
// (length-only) because supplier CNPJ/phone are optional fields and pre-
// existing rows in prod may have malformed values that pre-date the strict
// check-digit validation — applying the strict variants here would block
// edits to unrelated fields. Use `isValidCnpj` / `isValidPhone` from
// @mustauto/utils on first-class forms (workshop onboarding, partner
// registration) where strict validation is the goal.

function maskPaymentTerms(value: string): string {
  return value.replace(/[^0-9/,+ ]/g, '');
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isValidUrl(value: string): boolean {
  return /^(https?:\/\/)?[\w.-]+\.\w{2,}(\/.*)?$/.test(value);
}

function isValidCnpj(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length === 0 || digits.length === 14;
}

function isValidPhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length === 0 || digits.length === 10 || digits.length === 11;
}

export function SuppliersTab() {
  const { isDemo } = useAuth();
  const canManage = useCan()('fornecedores.gerir');
  const { suppliers, isLoading, isError, error, refetch, create, update, remove, isCreating, isUpdating, isRemoving } = useSuppliers();
  const { suppliers: insightsSuppliers, isLoadingSuppliers: isLoadingPartners } = useInventoryInsights();
  const [showForm, setShowForm] = useState(false);
  const [editSupplier, setEditSupplier] = useState<Supplier | null>(null);
  const [drawerSupplier, setDrawerSupplier] = useState<Supplier | null>(null);
  const [search, setSearch] = useState('');

  const partnerSuppliers = insightsSuppliers.filter((s) => s.isPartner);
  const partnerNames = new Set(partnerSuppliers.map((p) => p.name.toLowerCase()));

  const filteredSuppliers = suppliers.filter(
    (s) => s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.cnpj && s.cnpj.includes(search))
  );

  return (
    <div className="space-y-4">
      {/* Partner suppliers — recommended via active campaigns */}
      {!isLoadingPartners && partnerSuppliers.length > 0 && (
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="mb-3 flex items-center gap-2">
              <h3 className="text-sm font-semibold">Parceiros disponíveis</h3>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Info size={12} />
                Patrocinado
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {partnerSuppliers.map((p) => (
                <div
                  key={p.campaignId ?? p.name}
                  className="flex items-center gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5"
                >
                  <span className="text-sm font-medium">{p.name}</span>
                  <span className={PARTNER_BADGE_CLASSES}>[PARCEIRO]</span>
                  {p.tier && (
                    <Badge variant="info" className="text-[10px] uppercase">
                      {p.tier}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Estes fornecedores são parceiros comerciais MustAuto e podem
              aparecer como sugestão ao criar uma ordem de compra.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-xs font-medium text-muted-foreground">Buscar</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nome ou CNPJ"
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm w-64"
              />
            </div>
            {!isDemo && canManage && (
              <button
                onClick={() => { setShowForm(true); setEditSupplier(null); }}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Plus size={16} />
                Novo fornecedor
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : isError && suppliers.length === 0 ? (
        <Card>
          <CardContent>
            <ErrorState title="Não foi possível carregar os fornecedores" error={error} onRetry={() => refetch()} />
          </CardContent>
        </Card>
      ) : filteredSuppliers.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Building2 size={32} />}
              title={search ? 'Nenhum fornecedor encontrado' : 'Cadastre seu primeiro fornecedor'}
              description={
                search
                  ? `Sua busca por "${search}" não retornou resultados. Tente outro termo ou cadastre um novo fornecedor.`
                  : 'Mantenha o cadastro de fornecedores pra gerar ordens de compra, acompanhar prazos médios e priorizar parceiros recomendados.'
              }
              action={{
                label: 'Cadastrar fornecedor',
                onClick: () => { setShowForm(true); setEditSupplier(null); },
              }}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>CNPJ</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Cond. Pagamento</TableHead>
                <TableHead className="text-right">Em aberto</TableHead>
                <TableHead className="text-right">Prazo médio</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSuppliers.map((supplier) => (
                <TableRow
                  key={supplier.id}
                  className={`cursor-pointer hover:bg-muted/50 ${!supplier.active ? 'opacity-50' : ''}`}
                  onClick={() => setDrawerSupplier(supplier)}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span>{supplier.name}</span>
                      {partnerNames.has(supplier.name.toLowerCase()) && (
                        <>
                          <span className={PARTNER_BADGE_CLASSES}>[PARCEIRO]</span>
                          <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground">
                            <Info size={10} />
                            Patrocinado
                          </span>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground font-mono">
                    {supplier.cnpj ? maskCnpj(supplier.cnpj) : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {supplier.phone && (
                        <span className="flex items-center gap-1"><Phone size={12} />{maskPhone(supplier.phone)}</span>
                      )}
                      {supplier.email && (
                        <span className="flex items-center gap-1"><Mail size={12} />{supplier.email}</span>
                      )}
                      {!supplier.phone && !supplier.email && '—'}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{supplier.paymentTerms ?? '—'}</TableCell>
                  <TableCell className="text-right text-sm">
                    {supplier.openPayableTotal == null || supplier.openPayableTotal === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span
                        className={
                          supplier.overduePayableCount
                            ? 'font-semibold text-red-600 dark:text-red-400'
                            : 'font-medium'
                        }
                        title={
                          supplier.overduePayableCount
                            ? `${supplier.overduePayableCount} título(s) vencido(s)`
                            : undefined
                        }
                      >
                        {formatReais(supplier.openPayableTotal)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {supplier.avgLeadDays ? `${supplier.avgLeadDays} dias` : '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={supplier.active ? 'success' : 'secondary'}>
                      {supplier.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {!canManage ? null : supplier.active ? (
                        <>
                          <button
                            onClick={() => { setEditSupplier(supplier); setShowForm(true); }}
                            className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Editar
                          </button>
                          <button
                            onClick={async () => { await update({ id: supplier.id, active: false }); }}
                            disabled={isUpdating}
                            className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                          >
                            Desativar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={async () => { await update({ id: supplier.id, active: true }); }}
                            disabled={isUpdating}
                            className="rounded-md px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40 transition-colors disabled:opacity-50"
                          >
                            Reativar
                          </button>
                          <button
                            onClick={async () => { await remove(supplier.id); }}
                            disabled={isRemoving}
                            className="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40 transition-colors disabled:opacity-50"
                          >
                            Excluir
                          </button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Form Modal */}
      {showForm && (
        <SupplierFormModal
          supplier={editSupplier}
          onSubmit={async (data) => {
            if (editSupplier) {
              await update({ id: editSupplier.id, ...data });
            } else {
              await create(data);
            }
            setShowForm(false);
            setEditSupplier(null);
          }}
          onClose={() => { setShowForm(false); setEditSupplier(null); }}
          isSubmitting={isCreating || isUpdating}
        />
      )}

      {/* Drawer */}
      {drawerSupplier && (
        <SupplierDrawer
          supplier={drawerSupplier}
          onClose={() => setDrawerSupplier(null)}
          onEdit={(s) => { setDrawerSupplier(null); setEditSupplier(s); setShowForm(true); }}
        />
      )}
    </div>
  );
}

function SupplierFormModal({
  supplier,
  onSubmit,
  onClose,
  isSubmitting,
}: {
  supplier: Supplier | null;
  onSubmit: (data: {
    name: string;
    cnpj?: string | null;
    phone?: string | null;
    email?: string | null;
    website?: string | null;
    paymentTerms?: string | null;
    avgLeadDays?: number | null;
    notes?: string | null;
  }) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}) {
  useEscapeKey(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [name, setName] = useState(supplier?.name ?? '');
  const [cnpj, setCnpj] = useState(supplier?.cnpj ? maskCnpj(supplier.cnpj) : '');
  const [phone, setPhone] = useState(supplier?.phone ? maskPhone(supplier.phone) : '');
  const [email, setEmail] = useState(supplier?.email ?? '');
  const [website, setWebsite] = useState(supplier?.website ?? '');
  const [paymentTerms, setPaymentTerms] = useState(supplier?.paymentTerms ?? '');
  const [avgLeadDays, setAvgLeadDays] = useState(supplier?.avgLeadDays ? String(supplier.avgLeadDays) : '');
  const [notes, setNotes] = useState(supplier?.notes ?? '');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const errors: Record<string, string> = {};
  // Lenient por padrão (linha legada pode ter CNPJ malformado e travaria a
  // edição de campos não relacionados), estrito quando o usuário de fato
  // mexeu no CNPJ — aí não há motivo pra aceitar dígito verificador errado.
  const cnpjWasEdited = cnpj.replace(/\D/g, '') !== (supplier?.cnpj ?? '').replace(/\D/g, '');
  if (cnpj && !isValidCnpj(cnpj)) {
    errors.cnpj = 'CNPJ incompleto (14 dígitos)';
  } else if (cnpj && cnpjWasEdited && !isValidCnpjStrict(cnpj)) {
    errors.cnpj = 'CNPJ inválido — confira os dígitos';
  }
  if (phone && !isValidPhone(phone)) errors.phone = 'Telefone incompleto';
  if (email && !isValidEmail(email)) errors.email = 'E-mail inválido';
  if (website && !isValidUrl(website)) errors.website = 'URL inválida';
  if (avgLeadDays && (Number(avgLeadDays) < 0 || !Number.isInteger(Number(avgLeadDays)))) errors.avgLeadDays = 'Deve ser um número inteiro positivo';

  const hasErrors = Object.keys(errors).length > 0;
  const markTouched = (field: string) => setTouched((t) => ({ ...t, [field]: true }));
  const fieldError = (field: string) => touched[field] ? errors[field] : undefined;

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold">
              {supplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}
            </h3>
            <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted"><X size={18} /></button>
          </div>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (hasErrors) return;
              const rawCnpj = cnpj.replace(/\D/g, '') || null;
              const rawPhone = phone.replace(/\D/g, '') || null;
              await onSubmit({
                name,
                cnpj: rawCnpj,
                phone: rawPhone,
                email: email || null,
                website: website || null,
                paymentTerms: paymentTerms || null,
                avgLeadDays: avgLeadDays ? Number(avgLeadDays) : null,
                notes: notes || null,
              });
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1 sm:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Nome *</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">CNPJ</label>
                <input type="text" value={cnpj} onChange={(e) => setCnpj(maskCnpj(e.target.value))}
                  onBlur={() => markTouched('cnpj')} maxLength={18}
                  placeholder="00.000.000/0000-00"
                  className={`rounded-md border bg-background px-3 py-2 text-sm font-mono ${fieldError('cnpj') ? 'border-red-500' : 'border-input'}`} />
                {fieldError('cnpj') && <span className="text-xs text-red-500">{fieldError('cnpj')}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Telefone</label>
                <input type="text" value={phone} onChange={(e) => setPhone(maskPhone(e.target.value))}
                  onBlur={() => markTouched('phone')} maxLength={15}
                  placeholder="(00) 00000-0000"
                  className={`rounded-md border bg-background px-3 py-2 text-sm ${fieldError('phone') ? 'border-red-500' : 'border-input'}`} />
                {fieldError('phone') && <span className="text-xs text-red-500">{fieldError('phone')}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">E-mail</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => markTouched('email')}
                  placeholder="contato@fornecedor.com.br"
                  className={`rounded-md border bg-background px-3 py-2 text-sm ${fieldError('email') ? 'border-red-500' : 'border-input'}`} />
                {fieldError('email') && <span className="text-xs text-red-500">{fieldError('email')}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Website</label>
                <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)}
                  onBlur={() => markTouched('website')}
                  placeholder="https://www.fornecedor.com.br"
                  className={`rounded-md border bg-background px-3 py-2 text-sm ${fieldError('website') ? 'border-red-500' : 'border-input'}`} />
                {fieldError('website') && <span className="text-xs text-red-500">{fieldError('website')}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Condições de pagamento</label>
                <input type="text" value={paymentTerms} onChange={(e) => setPaymentTerms(maskPaymentTerms(e.target.value))}
                  placeholder="Ex: 30/60/90"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Prazo médio (dias)</label>
                <input type="number" value={avgLeadDays} onChange={(e) => setAvgLeadDays(e.target.value)}
                  onBlur={() => markTouched('avgLeadDays')} min={0} max={365}
                  placeholder="Ex: 7"
                  className={`rounded-md border bg-background px-3 py-2 text-sm ${fieldError('avgLeadDays') ? 'border-red-500' : 'border-input'}`} />
                {fieldError('avgLeadDays') && <span className="text-xs text-red-500">{fieldError('avgLeadDays')}</span>}
              </div>
              <div className="flex flex-col gap-1 sm:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Observações</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} rows={3}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button type="submit" disabled={isSubmitting || !name.trim() || hasErrors}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">
                {isSubmitting ? 'Salvando...' : supplier ? 'Salvar alterações' : 'Cadastrar fornecedor'}
              </button>
              <button type="button" onClick={onClose}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Cancelar
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function SupplierDrawer({
  supplier,
  onClose,
  onEdit,
}: {
  supplier: Supplier;
  onClose: () => void;
  onEdit: (s: Supplier) => void;
}) {
  useEscapeKey(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const canManage = useCan()('fornecedores.gerir');
  const [tab, setTab] = useState<'info' | 'orders' | 'payables'>('info');
  const { orders, isLoading: ordersLoading } = usePurchaseOrders(
    tab === 'orders' ? { supplierId: supplier.id, limit: 50 } : { supplierId: '__skip__', limit: 0 },
  );
  const {
    payables,
    openTotal: payablesOpenTotal,
    overdueTotal: payablesOverdueTotal,
    isLoading: payablesLoading,
    isError: payablesError,
    refetch: refetchPayables,
  } = usePayables(tab === 'payables' ? { supplierId: supplier.id } : {});

  const activeOrders = tab === 'orders' ? orders : [];

  // Summary stats from orders. Canceladas e rascunhos ficam de fora do valor:
  // antes o card somava as 50 últimas OCs em qualquer status, e o número não
  // significava nem compra feita nem dívida — era só uma soma.
  const totalOrders = activeOrders.length;
  const totalValue = activeOrders
    .filter((o) => o.status !== 'cancelled' && o.status !== 'draft')
    .reduce((sum, o) => sum + (o.totalAmount ?? 0), 0);

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-[480px] bg-background shadow-xl overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background p-4">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-lg font-semibold truncate">{supplier.name}</h2>
            <Badge variant={supplier.active ? 'success' : 'secondary'}>
              {supplier.active ? 'Ativo' : 'Inativo'}
            </Badge>
          </div>
          <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted shrink-0"><X size={18} /></button>
        </div>

        {/* Action */}
        <div className="flex gap-2 border-b p-4">
          {canManage && (
            <button
              onClick={() => onEdit(supplier)}
              className="rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
            >
              Editar
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {(['info', 'orders', 'payables'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === t
                  ? 'border-b-2 border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'info' ? 'Informações' : t === 'orders' ? 'Ordens de Compra' : 'A Pagar'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {tab === 'info' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <InfoField label="CNPJ" value={supplier.cnpj ?? '—'} />
                <InfoField label="Cadastro" value={new Date(supplier.createdAt).toLocaleDateString('pt-BR')} />
                {supplier.phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone size={14} className="text-muted-foreground" />
                    <span className="text-sm">{supplier.phone}</span>
                  </div>
                )}
                {supplier.email && (
                  <div className="flex items-center gap-1.5">
                    <Mail size={14} className="text-muted-foreground" />
                    <span className="text-sm">{supplier.email}</span>
                  </div>
                )}
                {supplier.website && (
                  <div className="flex items-center gap-1.5 col-span-2">
                    <Globe size={14} className="text-muted-foreground" />
                    <span className="text-sm">{supplier.website}</span>
                  </div>
                )}
              </div>
              <div className="rounded-lg border p-4 space-y-3">
                <h3 className="text-sm font-semibold">Detalhes comerciais</h3>
                <div className="grid grid-cols-2 gap-3">
                  <InfoField label="Cond. pagamento" value={supplier.paymentTerms ?? '—'} />
                  <InfoField label="Prazo médio" value={supplier.avgLeadDays ? `${supplier.avgLeadDays} dias` : '—'} />
                </div>
              </div>
              {supplier.notes && (
                <div className="rounded-lg border p-4">
                  <h3 className="text-sm font-semibold mb-2">Observações</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">{supplier.notes}</p>
                </div>
              )}
            </>
          )}

          {tab === 'orders' && (
            ordersLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : activeOrders.length === 0 ? (
              <EmptyState
                compact
                icon={<FileText size={22} />}
                title="Nenhuma ordem de compra"
                description="As OCs emitidas para este fornecedor aparecem aqui."
              />
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Total de OCs</div>
                    <div className="text-xl font-bold">{totalOrders}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Valor pedido</div>
                    <div className="text-xl font-bold">{activeOrders.some((o) => o.totalAmount == null) ? '—' : formatReais(totalValue)}</div>
                  </div>
                </div>

                {/* Orders list */}
                <div className="space-y-2">
                  {activeOrders.map((order) => (
                    <div key={order.id} className="rounded-lg border px-3 py-2.5 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">OC #{order.number}</span>
                        <POStatusBadge status={order.status} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Itens: </span>
                          <span className="font-medium">{order.itemsCount}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Valor: </span>
                          <span className="font-medium">{order.totalAmount == null ? '—' : formatReais(order.totalAmount)}</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground/70">
                        {new Date(order.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )
          )}

          {tab === 'payables' && (
            payablesLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : payablesError ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Não foi possível carregar as contas a pagar.
                <button
                  onClick={() => refetchPayables()}
                  className="ml-2 font-medium text-primary underline-offset-2 hover:underline"
                >
                  Tentar de novo
                </button>
              </div>
            ) : payables.length === 0 ? (
              <EmptyState
                compact
                icon={<Receipt size={22} />}
                title="Nenhum título a pagar"
                description="Títulos nascem da ordem de compra confirmada, ou de lançamento avulso no Financeiro."
              />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Em aberto</div>
                    <div className="text-xl font-bold">{formatReais(payablesOpenTotal)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Vencido</div>
                    <div
                      className={`text-xl font-bold ${payablesOverdueTotal > 0 ? 'text-red-600 dark:text-red-400' : ''}`}
                    >
                      {formatReais(payablesOverdueTotal)}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {payables.map((p) => (
                    <div key={p.id} className="space-y-1.5 rounded-lg border px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium">{p.description}</span>
                        <Badge variant={PAYABLE_BADGE_VARIANT[p.status]}>
                          {PAYABLE_STATUS_LABELS[p.status]}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Vencimento: </span>
                          <span className="font-medium">
                            {p.dueDate ? p.dueDate.split('-').reverse().join('/') : '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Em aberto: </span>
                          <span className="font-medium">{formatReais(p.remaining)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-center text-xs text-muted-foreground">
                  Para registrar pagamentos, use Financeiro → Contas a Pagar.
                </p>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
